import { ReactNode } from 'react'

import {
  Box,
  BoxProps,
  Divider,
  Flex,
  List,
  ListIcon,
  ListItem,
  Text,
} from '@chakra-ui/react'
import { isNumber } from 'lodash'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'

import { CheckIcon } from 'components/icons/CheckIcon'
import { SubscriptionPlans } from 'constants/subscription'
import {
  SubscriptionInterval,
  SubscriptionQuery,
} from 'generated/generated-graphql'
import { useRegion } from 'providers/RegionProvider'
import { Colors } from 'theme/constants'
import { getLocaleFormattedPrice } from 'utils/price'

import { SUBSCRIPTION_OVERVIEW_CARD_HEIGHT } from './constants'
import { Tag } from './Tag'

interface PlanCardProps extends BoxProps {
  planTitle: string
  buttonLabel: string
  planPrice?: number
  isEnterprisePlan?: boolean
  upgradeButton?: ReactNode
  subscriptionData?: SubscriptionQuery['subscription']
  isLegacy?: boolean
}

export const PlanCard = ({
  planPrice,
  planTitle,
  upgradeButton,
  isLegacy,
  subscriptionData,
  ...props
}: PlanCardProps) => {
  const { t } = useTranslation('common')
  const { region } = useRegion()

  const currentPlan = SubscriptionPlans[region].find(
    (sub) => sub.subscriptionTier === subscriptionData?.subscriptionTier
  )

  return (
    <Flex
      w="full"
      minH={`${SUBSCRIPTION_OVERVIEW_CARD_HEIGHT}px`}
      flexDirection="column"
      justifyContent="space-between"
      borderRadius="5px"
      border={`1px solid ${Colors.brand.gainsboro}`}
      p="24px"
      {...props}
    >
      <Box>
        <Flex gap="8px">
          <Tag>{t('TRANSLATION_376')}</Tag>
          {isLegacy && (
            <Tag
              bg="brand.secondary.orange100"
              color="brand.secondary.orange700"
              border="unset"
            >
              {t('legacy')}
            </Tag>
          )}
        </Flex>

        <Flex justifyContent="space-between" alignItems="baseline" mt="8px">
          <Text
            fontSize="24px"
            fontWeight={600}
            lineHeight="28px"
            color="brand.ultramarine"
          >
            {planTitle}
          </Text>

          {isNumber(planPrice) && (
            <Box ml="10px">
              <Text
                textAlign="right"
                fontSize="24px"
                fontWeight={600}
                lineHeight="24px"
              >
                {getLocaleFormattedPrice({ price: planPrice, region })}
              </Text>

              <Text textAlign="right" fontSize="16px" lineHeight="20px">
                {t('PER')}{' '}
                {subscriptionData?.interval === SubscriptionInterval.Year
                  ? t('year')
                  : t('month')}
              </Text>
            </Box>
          )}
        </Flex>

        <Divider mt="10px" />

        <List my="12px" spacing="10px" textAlign="left">
          {currentPlan?.bulletPoints?.map((bulletPoint) => (
            <ListItem
              key={bulletPoint}
              w="full"
              fontSize="16px"
              lineHeight="20px"
            >
              <ListIcon
                as={CheckIcon}
                color="brand.ultramarine"
                w="16px"
                h="16px"
              />
              <Trans
                i18nKey={`common:${bulletPoint}`}
                components={{
                  bold: <b />,
                }}
              />
            </ListItem>
          ))}
        </List>

        {isNumber(currentPlan?.successFee) && currentPlan.successFee > 0 && (
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mt="12px"
            w="full"
            fontSize="16px"
          >
            <Text lineHeight="20px">{t('TRANSLATION_330')}</Text>
            <Text lineHeight="20px" my="16px" fontWeight="bold">
              {getLocaleFormattedPrice({
                price: currentPlan.successFee,
                region,
              })}
            </Text>
          </Flex>
        )}
      </Box>
      {upgradeButton}
    </Flex>
  )
}
